import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import '../features/settings/styles/Settings.css';
import './styles/SuperAdmin.css';
import Loader from '../components/UI/Feedback/Loader';
import { PrimaryButton, SecondaryButton } from '../components/UI/Buttons';

const SuperAdminLogin = () => {
  const [email,          setEmail]          = useState('');
  const [password,       setPassword]       = useState('');
  const [rememberMe,     setRememberMe]     = useState(false);
  const [error,          setError]          = useState('');
  const [loading,        setLoading]        = useState(false);
  const [logoLoaded,     setLogoLoaded]     = useState(false);
  const [resetSent,      setResetSent]      = useState(false);
  const [resetLoading,   setResetLoading]   = useState(false);
  const [resetError,     setResetError]     = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail      = localStorage.getItem('superAdminEmail');
    const savedRememberMe = localStorage.getItem('superAdminRememberMe') === 'true';
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult    = await userCredential.user.getIdTokenResult(true);
      const claims         = tokenResult.claims;

      if (!claims.isSuperAdmin) {
        setError('Access denied. SuperAdmin privileges required.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      if (rememberMe) {
        localStorage.setItem('superAdminEmail',      email);
        localStorage.setItem('superAdminRememberMe', 'true');
      } else {
        localStorage.removeItem('superAdminEmail');
        localStorage.removeItem('superAdminRememberMe');
      }

      navigate('/admin', { replace: true });

    } catch (err) {
      const messages = {
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/user-not-found':     'No account found with this email.',
        'auth/wrong-password':     'Incorrect password.',
        'auth/too-many-requests':  'Too many failed attempts. Please try again later.'
      };
      setError(messages[err.code] || 'Authentication error. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setResetError('Enter your email address above first.');
      return;
    }
    setResetError('');
    setResetSent(false);
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setResetError(
        err.code === 'auth/user-not-found'
          ? 'No account found with this email.'
          : 'Failed to send reset email. Try again.'
      );
    } finally {
      setResetLoading(false);
    }
  };

  const showLoader = loading || !logoLoaded;

  return (
    <div className="login-modal login-modal-admin" style={{ position: 'relative' }}>

      {showLoader && (
        <div className="login-loader-overlay">
          <Loader />
        </div>
      )}

      <form onSubmit={handleLogin}>

        {/* Logo */}
        <div className="settings-form-field" style={{ textAlign: 'center' }}>
          <img
            src="/images/flow-logo-tmp-branded.jpeg"
            className="logo"
            alt="Logo"
            style={{
              minWidth: 250, minHeight: 250,
              objectFit: 'contain',
              margin: '0 auto 6px auto',
              display: 'block'
            }}
            onLoad ={() => setLogoLoaded(true)}
            onError={() => setLogoLoaded(true)}
          />
        </div>
    <div className="logo-centered">
          <div className="small-brand superadmin-badge">
            superadmin
          </div>
        </div>
        {/* Email */}
        <div className="settings-form-field">
          <label htmlFor="email">E-mail</label>
          <input
            type="email"
            id="email"
            className="textField"
            placeholder="e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>

        {/* Password */}
        <div className="settings-form-field">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            className="textField"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
     {/* Remember me */}
        <label className="remember-me-label-login" style={{ background: 'transparent', paddingRight:'20px' }}>
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <span>Remember me</span>
        </label>
        {/* Password recovery */}
        <div className="login-password-recovery">
          <button
            type="button"
            className="login-recovery-link"
            onClick={handlePasswordReset}
            disabled={resetLoading}
          >
            {resetLoading ? 'Sending...' : 'Forgot password?'}
          </button>
          {resetSent  && <span className="login-recovery-success">Reset email sent ✓</span>}
          {resetError && <span className="login-recovery-error">{resetError}</span>}
        </div>

   

        {error && <div className="login-error-message">{error}</div>}
<div style={{justifyContent:'center', display:'flex'}}>
        <div className="login-button-group" style={{width:'200px', marginTop:0}}>
          <PrimaryButton
            type="submit"
            disabled={loading}
            onClick={handleLogin}
            icon='login'
            text='Log In'
          >
            {loading ? 'Authenticating...' : 'Log In'}
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={() => navigate('/')}
            icon='arrow_back'
            text='Back to Home'
          >
            ← Back to Home
          </SecondaryButton>
        </div></div>

        {/* Footer note */}
        <p className="superadmin-footer-note">
          Restricted access. All actions are logged and monitored.
        </p>

      </form>
    </div>
  );
};

export default SuperAdminLogin;